/**
 * Psyche Voice Types
 *
 * GM-curated internal voice system. The AI suggests "psyche thoughts"
 * based on game context; the GM decides when and to whom to deliver them.
 * Each voice represents an aspect of a character's inner life.
 */
import { z } from 'zod';
// =============================================================================
// PSYCHE VOICES
// =============================================================================
/**
 * The eight psyche voices — aspects of a character's inner life.
 * Each maps to a D&D ability score or skill cluster.
 */
export const PsycheVoiceSchema = z.enum([
    'EMPATHY', // Reads emotions, detects lies, feels others' pain (WIS/CHA)
    'LOGIC', // Deductive reasoning, pattern recognition (INT)
    'INSTINCT', // Gut feelings, danger sense, animal awareness (WIS/CON)
    'RHETORIC', // Persuasion, manipulation, political awareness (CHA)
    'PERCEPTION', // Notices details, hidden things, sensory input (WIS)
    'MEMORY', // Past connections, deja vu, historical knowledge (INT)
    'VOLITION', // Willpower, self-control, resistance to temptation (WIS/CON)
    'SUGGESTION', // Social manipulation, what-if scenarios, temptation (CHA)
]);
/** Display metadata for each psyche voice */
export const PSYCHE_VOICE_META = {
    EMPATHY: {
        label: 'Empathy',
        description: 'Reads emotions, detects lies, feels others\' pain',
        color: '#e879a0',
        iconHint: 'heart',
        abilityLink: 'WIS/CHA',
    },
    LOGIC: {
        label: 'Logic',
        description: 'Deductive reasoning, pattern recognition, cold analysis',
        color: '#64b5f6',
        iconHint: 'crystal',
        abilityLink: 'INT',
    },
    INSTINCT: {
        label: 'Instinct',
        description: 'Gut feelings, danger sense, animal awareness',
        color: '#ff8a65',
        iconHint: 'alert',
        abilityLink: 'WIS/CON',
    },
    RHETORIC: {
        label: 'Rhetoric',
        description: 'Persuasion, argumentation, political awareness',
        color: '#ce93d8',
        iconHint: 'scroll',
        abilityLink: 'CHA',
    },
    PERCEPTION: {
        label: 'Perception',
        description: 'Notices details, hidden things, sensory input',
        color: '#4db6ac',
        iconHint: 'eye',
        abilityLink: 'WIS',
    },
    MEMORY: {
        label: 'Memory',
        description: 'Past connections, deja vu, historical echoes',
        color: '#a1887f',
        iconHint: 'moon',
        abilityLink: 'INT',
    },
    VOLITION: {
        label: 'Volition',
        description: 'Willpower, self-control, inner strength',
        color: '#ffd54f',
        iconHint: 'shield',
        abilityLink: 'WIS/CON',
    },
    SUGGESTION: {
        label: 'Suggestion',
        description: 'What-if scenarios, temptation, the devil on your shoulder',
        color: '#81c784',
        iconHint: 'wand',
        abilityLink: 'CHA',
    },
};
// =============================================================================
// PSYCHE DEPTH (the slider)
// =============================================================================
/**
 * Controls how verbose and frequent psyche thoughts are.
 * GM sets this per campaign via a slider.
 */
export const PsycheDepthSchema = z.enum([
    'FLAVOR', // Occasional color text, 1-2 per scene
    'ATMOSPHERIC', // Mood-setting thoughts, 3-4 per scene
    'INSIGHT', // Reveals hidden connections, 5+ per scene
    'DEEP_PSYCHE', // Full internal monologue, competing voices
]);
export const PSYCHE_DEPTH_LABELS = {
    FLAVOR: {
        label: 'Flavor',
        description: 'Light touch — occasional atmospheric color',
    },
    ATMOSPHERIC: {
        label: 'Atmospheric',
        description: 'Mood-setting thoughts that enrich scenes',
    },
    INSIGHT: {
        label: 'Insight',
        description: 'Reveals hidden connections and deeper meaning',
    },
    DEEP_PSYCHE: {
        label: 'Deep Psyche',
        description: 'Full internal dialogue — competing voices, inner debates',
    },
};
// =============================================================================
// THOUGHT INTENSITY
// =============================================================================
export const ThoughtIntensitySchema = z.enum([
    'whisper', // Translucent, italic, fades quickly
    'thought', // Standard card with voice color border
    'shout', // Full-width banner, bold, slight glow
]);
// =============================================================================
// PSYCHE THOUGHT
// =============================================================================
export const PsycheThoughtStatusSchema = z.enum([
    'pending', // In GM queue, awaiting review
    'approved', // GM approved, ready to deliver
    'rejected', // GM rejected
    'edited', // GM edited content
    'delivered', // Sent to player
    'read', // Player acknowledged
]);
export const SuggestedTimingSchema = z.enum([
    'immediate', // Show as soon as GM approves
    'next_pause', // Wait for a lull in action
    'when_relevant', // GM decides when context fits
]);
export const PsycheThoughtSchema = z.object({
    id: z.string(),
    voice: PsycheVoiceSchema,
    content: z.string(), // The thought text
    context: z.string().optional(), // What triggered it
    intensity: ThoughtIntensitySchema.default('thought'),
    targetCharacterId: z.string().nullable().default(null), // Which PC, or null for GM-only
    factionRelevance: z.string().nullable().default(null), // Faction ID if relevant
    suggestedTiming: SuggestedTimingSchema.default('when_relevant'),
    status: PsycheThoughtStatusSchema.default('pending'),
    // Delivery tracking
    deliveredTo: z.string().nullable().default(null),
    deliveredAt: z.string().nullable().default(null),
    displayDuration: z.number().default(5), // Seconds
    wasRead: z.boolean().default(false),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
// =============================================================================
// PSYCHE CONFIG (per-campaign settings)
// =============================================================================
export const PsycheConfigSchema = z.object({
    depth: PsycheDepthSchema.default('ATMOSPHERIC'),
    enabledVoices: z.array(PsycheVoiceSchema).default([
        'EMPATHY', 'LOGIC', 'INSTINCT', 'RHETORIC',
        'PERCEPTION', 'MEMORY', 'VOLITION', 'SUGGESTION',
    ]),
    voiceFrequency: z.number().min(0).max(100).default(50), // How eagerly AI generates
    perCharacterOverrides: z.record(z.string(), z.object({
        enabledVoices: z.array(PsycheVoiceSchema).optional(),
        depth: PsycheDepthSchema.optional(),
    })).default({}),
});
// =============================================================================
// DELIVERED THOUGHT (player-facing record)
// =============================================================================
export const DeliveredThoughtSchema = z.object({
    thoughtId: z.string(),
    voice: PsycheVoiceSchema,
    content: z.string(),
    intensity: ThoughtIntensitySchema,
    deliveredTo: z.string(), // Character ID
    deliveredAt: z.string(), // ISO date
    displayDuration: z.number(),
    wasRead: z.boolean().default(false),
    factionRelevance: z.string().nullable().default(null),
});
//# sourceMappingURL=psyche-types.js.map